/* eslint-disable react-refresh/only-export-components -- context module: exports a provider + hook, not a Fast-Refresh component file */
import { createContext, use } from "react";

import type { ProviderConfig } from "@/lib/types/itsm-config";

/**
 * The whole ITSM config tab edits a single in-memory draft; nothing persists
 * until the page-level Save. Cards read the current draft `providerConfig`
 * (passed down as usual) and mutate it via `update`, which merges a partial
 * patch onto the draft. `update` accepts either a patch object or an updater
 * function of the current draft (for read-modify-write like general_mapping).
 */
export interface DraftContextValue {
  update: (
    patch:
      | Partial<ProviderConfig>
      | ((current: ProviderConfig) => Partial<ProviderConfig>),
  ) => void;
  /** Top-level config tags (sibling of provider_config), drafted alongside. */
  tags: Record<string, string> | null;
  setTags: (next: Record<string, string> | null) => void;
  /** Applies a change the backend already saved - to the draft and to the
   *  saved baseline alike - so a later Save keeps it and it reads as clean. */
  applySaved: (
    patch: (current: ProviderConfig) => Partial<ProviderConfig>,
  ) => void;
  /** Whether the draft holds edits not yet saved. */
  dirty: boolean;
}

const DraftContext = createContext<DraftContextValue | null>(null);

export const DraftProvider = DraftContext.Provider;

export function useDraft(): DraftContextValue {
  const ctx = use(DraftContext);
  if (!ctx) {
    throw new Error("useDraft must be used within a DraftProvider");
  }
  return ctx;
}
